import React, { useState } from 'react';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { FormField } from '../atoms/FormField';
import { getTotalKwhFromGroup } from '../../utils/tokens';
import type { MiningGroup } from '../../types/mining';

interface MiningGroupManagerProps {
  tokenId: string;
  tokenName: string;
  groups: MiningGroup[];
  onAddGroup: (group: Omit<MiningGroup, 'id'>) => void;
  onUpdateGroup: (groupId: string, updatedGroup: Partial<Omit<MiningGroup, 'id'>>) => void;
  onRemoveGroup: (groupId: string) => void;
}

type InputMode = 'consumption' | 'kwh_direct';

export const MiningGroupManager: React.FC<MiningGroupManagerProps> = ({
  tokenName,
  groups,
  onAddGroup,
  onUpdateGroup,
  onRemoveGroup,
}) => {
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>('consumption');
  const [newGroup, setNewGroup] = useState({
    powerConsumptionWh: '',
    miningDurationDays: '',
    totalKwhDirect: '',
    description: '',
  });

  const handleAddGroup = () => {
    if (inputMode === 'consumption') {
      if (newGroup.powerConsumptionWh && newGroup.miningDurationDays) {
        onAddGroup({
          powerConsumptionWh: parseFloat(newGroup.powerConsumptionWh),
          miningDurationDays: parseFloat(newGroup.miningDurationDays),
          description: newGroup.description || undefined,
        });
        resetForm();
      }
    } else if (inputMode === 'kwh_direct') {
      if (newGroup.totalKwhDirect) {
        onAddGroup({
          totalKwhDirect: parseFloat(newGroup.totalKwhDirect),
          description: newGroup.description || undefined,
        });
        resetForm();
      }
    }
  };

  const resetForm = () => {
    setNewGroup({
      powerConsumptionWh: '',
      miningDurationDays: '',
      totalKwhDirect: '',
      description: '',
    });
    setIsAddingGroup(false);
    setInputMode('consumption');
  };

  const handleUpdateGroup = (groupId: string, field: string, value: string) => {
    const numericFields = ['powerConsumptionWh', 'miningDurationDays', 'totalKwhDirect'];
    const updateValue = numericFields.includes(field) ? parseFloat(value) || 0 : value;

    onUpdateGroup(groupId, {
      [field]: updateValue,
    });
  };

  const totalKwh = groups.reduce((sum, group) => sum + getTotalKwhFromGroup(group), 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{tokenName}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {groups.length} groupe{groups.length > 1 ? 's' : ''} de minage
              {totalKwh > 0 && (
                <span className="ml-2">• {totalKwh.toFixed(2)} kWh total</span>
              )}
            </p>
          </div>
          <Button
            onClick={() => setIsAddingGroup(true)}
            variant="secondary"
            className="text-sm"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Ajouter groupe
          </Button>
        </div>
      </div>

      {/* Liste des groupes */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {groups.map((group) => {
          const isKwhMode = group.totalKwhDirect !== undefined && group.totalKwhDirect > 0;
          const groupKwh = getTotalKwhFromGroup(group);

          return (
            <div key={group.id} className="p-4">
              {isKwhMode ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <FormField label="kWh (total direct)" htmlFor={`kwh-${group.id}`}>
                    <Input
                      id={`kwh-${group.id}`}
                      type="number"
                      step="0.1"
                      min="0"
                      value={group.totalKwhDirect?.toString() || ''}
                      onChange={(e) => handleUpdateGroup(group.id, 'totalKwhDirect', e.target.value)}
                      className="text-sm"
                    />
                  </FormField>

                  <FormField label="Description (optionnel)" htmlFor={`desc-${group.id}`}>
                    <Input
                      id={`desc-${group.id}`}
                      type="text"
                      value={group.description || ''}
                      onChange={(e) => handleUpdateGroup(group.id, 'description', e.target.value)}
                      placeholder="Ex: Mineur A"
                      className="text-sm"
                    />
                  </FormField>

                  <div className="flex items-center space-x-2 justify-between">
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-yellow-50 px-2 py-1 rounded">
                      Mode direct: {typeof groupKwh === 'number' ? groupKwh.toFixed(2) : '0.00'} kWh
                    </div>
                    <Button
                      onClick={() => onRemoveGroup(group.id)}
                      variant="secondary"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 text-sm px-2 py-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                  <FormField label="Consommation (W/h)" htmlFor={`power-${group.id}`}>
                    <Input
                      id={`power-${group.id}`}
                      type="number"
                      step="1"
                      min="0"
                      value={group.powerConsumptionWh?.toString() || ''}
                      onChange={(e) => handleUpdateGroup(group.id, 'powerConsumptionWh', e.target.value)}
                      className="text-sm"
                    />
                  </FormField>

                  <FormField label="Durée (jours)" htmlFor={`duration-${group.id}`}>
                    <Input
                      id={`duration-${group.id}`}
                      type="number"
                      step="0.1"
                      min="0"
                      value={group.miningDurationDays?.toString() || ''}
                      onChange={(e) => handleUpdateGroup(group.id, 'miningDurationDays', e.target.value)}
                      className="text-sm"
                    />
                  </FormField>

                  <FormField label="Description (optionnel)" htmlFor={`desc-${group.id}`}>
                    <Input
                      id={`desc-${group.id}`}
                      type="text"
                      value={group.description || ''}
                      onChange={(e) => handleUpdateGroup(group.id, 'description', e.target.value)}
                      placeholder="Ex: Antminer S19"
                      className="text-sm"
                    />
                  </FormField>

                  <div className="flex items-center space-x-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {typeof groupKwh === 'number' ? groupKwh.toFixed(2) : '0.00'} kWh
                    </div>
                    <Button
                      onClick={() => onRemoveGroup(group.id)}
                      variant="secondary"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 text-sm px-2 py-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Formulaire d'ajout */}
        {isAddingGroup && (
          <div className="p-4 bg-blue-50 border-t border-blue-200">
            <h5 className="text-sm font-medium text-blue-900 mb-4">Nouveau groupe de minage</h5>

            {/* Mode selector */}
            <div className="mb-4 flex gap-2">
              <Button
                onClick={() => setInputMode('consumption')}
                size="sm"
                className={`flex-1 ${
                  inputMode === 'consumption'
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 hover:dark:bg-gray-900'
                }`}
              >
                Mode consommation
              </Button>
              <Button
                onClick={() => setInputMode('kwh_direct')}
                size="sm"
                className={`flex-1 ${
                  inputMode === 'kwh_direct'
                    ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 hover:dark:bg-gray-900'
                }`}
              >
                Mode kWh direct
              </Button>
            </div>

            {/* Consumption mode fields */}
            {inputMode === 'consumption' && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end mb-4">
                <FormField label="Consommation (W/h)" htmlFor="new-power">
                  <Input
                    id="new-power"
                    type="number"
                    step="1"
                    min="0"
                    value={newGroup.powerConsumptionWh}
                    onChange={(e) => setNewGroup(prev => ({ ...prev, powerConsumptionWh: e.target.value }))}
                    placeholder="300"
                    className="text-sm"
                  />
                </FormField>

                <FormField label="Durée (jours)" htmlFor="new-duration">
                  <Input
                    id="new-duration"
                    type="number"
                    step="0.1"
                    min="0"
                    value={newGroup.miningDurationDays}
                    onChange={(e) => setNewGroup(prev => ({ ...prev, miningDurationDays: e.target.value }))}
                    placeholder="30"
                    className="text-sm"
                  />
                </FormField>

                <FormField label="Description (optionnel)" htmlFor="new-desc">
                  <Input
                    id="new-desc"
                    type="text"
                    value={newGroup.description}
                    onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Ex: Antminer S19"
                    className="text-sm"
                  />
                </FormField>

                <div className="flex items-center space-x-2">
                  <Button
                    onClick={handleAddGroup}
                    disabled={!newGroup.powerConsumptionWh || !newGroup.miningDurationDays}
                    className="text-sm"
                  >
                    Ajouter
                  </Button>
                </div>
              </div>
            )}

            {/* kWh direct mode fields */}
            {inputMode === 'kwh_direct' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
                <FormField label="kWh (total direct)" htmlFor="new-kwh">
                  <Input
                    id="new-kwh"
                    type="number"
                    step="0.1"
                    min="0"
                    value={newGroup.totalKwhDirect}
                    onChange={(e) => setNewGroup(prev => ({ ...prev, totalKwhDirect: e.target.value }))}
                    placeholder="450"
                    className="text-sm"
                  />
                </FormField>

                <FormField label="Description (optionnel)" htmlFor="new-desc-kwh">
                  <Input
                    id="new-desc-kwh"
                    type="text"
                    value={newGroup.description}
                    onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Ex: Mineur A"
                    className="text-sm"
                  />
                </FormField>

                <div className="flex items-center space-x-2">
                  <Button
                    onClick={handleAddGroup}
                    disabled={!newGroup.totalKwhDirect}
                    className="text-sm"
                  >
                    Ajouter
                  </Button>
                </div>
              </div>
            )}

            {/* Cancel button (common) */}
            <div className="flex justify-end">
              <Button
                onClick={() => resetForm()}
                variant="secondary"
                className="text-sm"
              >
                Annuler
              </Button>
            </div>
          </div>
        )}

        {/* Message si aucun groupe */}
        {groups.length === 0 && !isAddingGroup && (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <p className="text-sm">Aucun groupe de minage configuré</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Cliquez sur &quot;Ajouter groupe&quot; pour commencer
            </p>
          </div>
        )}
      </div>
    </div>
  );
};